import { useState, useEffect, useRef, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Users, LogOut, Loader2, Search, Eye, Mail, Phone, Calendar, Clock,
  User, ChevronLeft, ChevronRight, Bell, BellOff, Volume2, VolumeX,
  MessageSquare, FileText, Send, ArrowLeft, Check, Filter, UserPlus, UserMinus,
  Mic, Play, Pause, Download
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Switch } from "../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Notification Sound
const NOTIFICATION_SOUND_URL = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2LkZeXkIuFf3x4d3Z4fIGHjJCTlJOQjImFgn97eHZ1dnh8gYaLj5KUlJKPjIiEgX56d3V1d3t/hImNkJOUk5GPi4eDgH15dnR0dnh8gIWJjZCTlJOQjYqGgn98eHV0dHZ5fYGFiY2QkpOSkI2KhoJ/fHh1dHR2eX2BhYmNkJKTkpCNioaCf3x4dXR0dnl9gYWJjZCRk5KQjYqGgn98eHV0dHZ5fYGFiY2QkpOSkI2Khg==";

const STATUS_LABELS = {
  new: "Neu",
  waitlist: "Nicht erreicht",
  contacted: "Kann erst später", 
  qualified: "Qualifiziert",
  unsuitable: "Ungeeignet",
  contract_sent: "Weitergeleitet an T2M",
  hired: "Eingestellt",
  duplicate: "Doppelt",
  abandoned: "Bedarf noch Klärung",
  appointment_scheduled: "Termin vereinbart",
  interview_done: "Interview erledigt",
  rejected: "Abgelehnt"
};

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700",
  waitlist: "bg-orange-100 text-orange-700",
  contacted: "bg-purple-100 text-purple-700",
  qualified: "bg-green-100 text-green-700",
  unsuitable: "bg-gray-100 text-gray-700",
  contract_sent: "bg-orange-100 text-orange-700",
  hired: "bg-emerald-100 text-emerald-700",
  duplicate: "bg-slate-100 text-slate-700",
  abandoned: "bg-red-100 text-red-700",
  appointment_scheduled: "bg-amber-100 text-amber-700",
  interview_done: "bg-teal-100 text-teal-700",
  rejected: "bg-red-100 text-red-700"
};

// Lead Notification Hook - Works in background tabs
const useLeadNotifications = (authToken, enabled, advisorProjectId) => {
  const lastLeadCountRef = useRef(null);
  const lastCheckTimeRef = useRef(null);
  const audioRef = useRef(null);
  
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio(NOTIFICATION_SOUND_URL);
        audioRef.current.volume = 0.7;
      }
      // Force play even in background
      const playPromise = audioRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          console.log("Audio play failed, retrying:", e);
          // Retry after a short delay
          setTimeout(() => {
            audioRef.current?.play().catch(() => {});
          }, 100);
        });
      }
    } catch (e) {
      console.log("Audio error:", e);
    }
  }, []);

  const showBrowserNotification = useCallback((leadName, projectName) => {
    if (Notification.permission === "granted") {
      const notification = new Notification("🔔 Neuer Lead!", {
        body: `${leadName || "Ein neuer Bewerber"} (${projectName}) wurde Ihnen zugewiesen!`,
        icon: "/favicon.ico",
        tag: "new-lead-" + Date.now(),
        requireInteraction: true,
        silent: false
      });
      
      // Auto-close after 30 seconds
      setTimeout(() => notification.close(), 30000);
      
      // Focus window when clicked
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  }, []);

  const checkForNewLeads = useCallback(async () => {
    if (!authToken || !enabled) return;
    
    try {
      // Check for leads assigned to this advisor
      const response = await axios.get(`${API}/advisor/leads?page=1&page_size=5`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      const currentCount = response.data.total;
      const leads = response.data.leads || [];
      
      // Initialize on first run
      if (lastLeadCountRef.current === null) {
        lastLeadCountRef.current = currentCount;
        lastCheckTimeRef.current = new Date().toISOString();
        return;
      }
      
      // Check if there are new leads
      if (currentCount > lastLeadCountRef.current) {
        // Find the newest lead
        const newLeadsCount = currentCount - lastLeadCountRef.current;
        const latestLead = leads[0];
        const leadName = latestLead?.first_name || "Neuer Bewerber";
        const projectName = latestLead?.project_id === "factoryhype" ? "FactoryHype" : "HypeFactory";
        
        // Play sound and show notification (works in background)
        playNotificationSound();
        showBrowserNotification(leadName, projectName);
        
        // Also show toast if tab is visible
        if (document.visibilityState === 'visible') {
          toast.success(`🔔 ${newLeadsCount > 1 ? newLeadsCount + ' neue Leads' : 'Neuer Lead: ' + leadName}!`, { 
            duration: 10000 
          });
        }
        
        lastLeadCountRef.current = currentCount;
      }
      
      lastCheckTimeRef.current = new Date().toISOString();
    } catch (error) {
      console.log("Lead check error:", error);
    }
  }, [authToken, enabled, playNotificationSound, showBrowserNotification]);

  useEffect(() => {
    if (!enabled) return;
    
    // Request notification permission
    if (Notification.permission === "default") {
      Notification.requestPermission();
    }
    
    // Initial check
    checkForNewLeads();
    
    // Check every 10 seconds - runs even when tab is in background
    const interval = setInterval(checkForNewLeads, 10000);
    
    // Also check when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkForNewLeads();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkForNewLeads]);
};

// Login Component
const AdvisorLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    
    try {
      const response = await axios.post(`${API}/advisor/login`, { email, password });
      localStorage.setItem("advisor_token", response.data.token);
      localStorage.setItem("advisor_name", response.data.advisor.name);
      localStorage.setItem("advisor_email", response.data.advisor.email);
      onLogin(response.data.token, response.data.advisor);
    } catch (error) {
      setError(error.response?.data?.detail || "Login fehlgeschlagen");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <Card className="bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">Berater Login</CardTitle>
            <p className="text-slate-500 text-sm">Factory-Hype Lead Management</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              {error && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="berater@firma.de"
                  className="rounded-lg"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-lg"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="w-full rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                disabled={isLoading}
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Anmelden
              </Button>
            </form>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

// Lead List Component
const LeadList = ({ authToken, onSelectLead, advisorId, advisorName }) => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignmentFilter, setAssignmentFilter] = useState(""); // "", "mine", "unassigned", "others"
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailContent, setEmailContent] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [claimingLeadId, setClaimingLeadId] = useState(null);
  
  // Template save states
  const [saveTemplateModalOpen, setSaveTemplateModalOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  
  // Status folder counts
  const [statusCounts, setStatusCounts] = useState({});

  // Manual Lead Creation States
  const [createLeadModalOpen, setCreateLeadModalOpen] = useState(false);
  const [isCreatingLead, setIsCreatingLead] = useState(false);
  const [newLead, setNewLead] = useState({
    first_name: "",
    email: "",
    phone: "",
    age: "",
    wohnort: "",
    start_date: "",
    job_type: "",
    travel_ready: null,
    full_time: null,
    zoom_call_ok: "",
    available_days: "",
    notes: "",
    status: "new"
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  const resetNewLeadForm = () => {
    setNewLead({
      first_name: "",
      email: "",
      phone: "",
      age: "",
      wohnort: "",
      start_date: "",
      job_type: "",
      travel_ready: null,
      full_time: null,
      zoom_call_ok: "",
      available_days: "",
      notes: "",
      status: "new"
    });
  };

  const handleCreateLead = async () => {
    if (!newLead.first_name.trim()) {
      toast.error("Name ist erforderlich");
      return;
    }
    if (!newLead.email.trim()) {
      toast.error("E-Mail ist erforderlich");
      return;
    }
    if (!newLead.phone.trim()) {
      toast.error("Telefon ist erforderlich");
      return;
    }

    setIsCreatingLead(true);
    try {
      const response = await axios.post(`${API}/advisor/leads/manual`, newLead, getAuthHeaders());
      
      if (response.data.success) {
        toast.success(`Lead "${newLead.first_name}" erfolgreich erstellt!`);
        setCreateLeadModalOpen(false);
        resetNewLeadForm();
        fetchLeads();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Leads");
    } finally {
      setIsCreatingLead(false);
    }
  };

  useEffect(() => {
    fetchLeads();
  }, [page, statusFilter, assignmentFilter]);

  const fetchLeads = async () => {
    setIsLoading(true);
    try {
      let url = `${API}/advisor/leads?page=${page}&page_size=25`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      
      const response = await axios.get(url, getAuthHeaders());
      let fetchedLeads = response.data.leads;
      
      // Client-side filtering for assignment (could be moved to backend)
      if (assignmentFilter === "mine") {
        fetchedLeads = fetchedLeads.filter(l => l.assigned_to === advisorId);
      } else if (assignmentFilter === "unassigned") {
        fetchedLeads = fetchedLeads.filter(l => !l.assigned_to);
      } else if (assignmentFilter === "others") {
        fetchedLeads = fetchedLeads.filter(l => l.assigned_to && l.assigned_to !== advisorId);
      }
      
      setLeads(fetchedLeads);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
      
      // Update status counts from all leads
      fetchStatusCounts();
    } catch (error) {
      toast.error("Fehler beim Laden der Leads");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatusCounts = async () => {
    try {
      // Get counts for all statuses
      const response = await axios.get(`${API}/advisor/leads/status-counts`, getAuthHeaders());
      setStatusCounts(response.data.counts || {});
    } catch (error) {
      console.log("Could not fetch status counts");
    }
  };

  const handleClaimLead = async (leadId, e) => {
    e.stopPropagation();
    setClaimingLeadId(leadId);
    try {
      await axios.post(`${API}/advisor/leads/${leadId}/claim`, {}, getAuthHeaders());
      toast.success("Lead übernommen!");
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Übernehmen");
    } finally {
      setClaimingLeadId(null);
    }
  };

  const handleReleaseLead = async (leadId, e) => {
    e.stopPropagation();
    setClaimingLeadId(leadId);
    try {
      await axios.post(`${API}/advisor/leads/${leadId}/release`, {}, getAuthHeaders());
      toast.success("Lead freigegeben!");
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Freigeben");
    } finally {
      setClaimingLeadId(null);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLeads();
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const openEmailModal = async () => {
    if (selectedLeads.length === 0) {
      toast.error("Bitte zuerst Leads auswählen");
      return;
    }
    try {
      const response = await axios.get(`${API}/advisor/email-templates`, getAuthHeaders());
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
    setEmailModalOpen(true);
  };

  const handleTemplateChange = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId && templateId !== "custom") {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setEmailSubject(template.subject);
        setEmailContent(template.html_content || template.content || "");
      }
    } else {
      setEmailSubject("");
      setEmailContent("");
    }
  };

  const handleSendEmail = async () => {
    if (!emailSubject || !emailContent) {
      toast.error("Bitte Betreff und Inhalt angeben");
      return;
    }

    setIsSendingEmail(true);
    try {
      const response = await axios.post(`${API}/advisor/leads/send-email`, {
        lead_ids: selectedLeads,
        template_id: selectedTemplate !== "custom" ? selectedTemplate : null,
        subject: emailSubject,
        html_content: emailContent
      }, getAuthHeaders());

      toast.success(`${response.data.sent_count} E-Mail(s) gesendet!`);
      setEmailModalOpen(false);
      setSelectedTemplate("");
      setEmailSubject("");
      setEmailContent("");
      setSelectedLeads([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Senden");
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Save email as template
  const handleSaveTemplate = async () => {
    if (!templateName || !emailSubject || !emailContent) {
      toast.error("Bitte Name, Betreff und Inhalt angeben");
      return;
    }
    
    setIsSavingTemplate(true);
    try {
      await axios.post(`${API}/advisor/email-templates`, {
        name: templateName,
        subject: emailSubject,
        html_content: emailContent
      }, getAuthHeaders());
      
      toast.success("Vorlage gespeichert!");
      setSaveTemplateModalOpen(false);
      setTemplateName("");
      
      // Refresh templates
      const response = await axios.get(`${API}/advisor/email-templates`, getAuthHeaders());
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setIsSavingTemplate(false);
    }
  };

  // Delete advisor template
  const handleDeleteTemplate = async (templateId) => {
    if (!confirm("Vorlage wirklich löschen?")) return;
    
    try {
      await axios.delete(`${API}/advisor/email-templates/${templateId}`, getAuthHeaders());
      toast.success("Vorlage gelöscht!");
      
      // Refresh templates
      const response = await axios.get(`${API}/advisor/email-templates`, getAuthHeaders());
      setTemplates(response.data.templates || []);
      
      // Reset selection if deleted template was selected
      if (selectedTemplate === templateId) {
        setSelectedTemplate("custom");
        setEmailSubject("");
        setEmailContent("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Project badge helper
  const getProjectBadge = (projectId) => {
    if (projectId === "factoryhype") {
      return <Badge className="bg-emerald-100 text-emerald-700 text-xs">FactoryHype</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700 text-xs">HypeFactory</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Status Folders - Anzahl IMMER anzeigen */}
      <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
        <Button
          variant={statusFilter === "" ? "default" : "ghost"}
          size="sm"
          onClick={() => { setStatusFilter(""); setPage(1); }}
          className={`rounded-lg ${statusFilter === "" ? "bg-slate-800" : ""}`}
        >
          Alle
          <Badge className={`ml-2 ${statusFilter === "" ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>
            {statusCounts._total || total || 0}
          </Badge>
        </Button>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <Button
            key={key}
            variant={statusFilter === key ? "default" : "ghost"}
            size="sm"
            onClick={() => { setStatusFilter(key); setPage(1); }}
            className={`rounded-lg ${statusFilter === key ? "bg-slate-800" : ""}`}
          >
            {label}
            <Badge className={`ml-2 ${statusFilter === key ? "bg-white/20 text-white" : "bg-slate-200 text-slate-700"}`}>
              {statusCounts[key] || 0}
            </Badge>
          </Button>
        ))}
      </div>

      {/* Header & Search */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Leads</h2>
          <p className="text-slate-500 text-sm">{total} Leads {statusFilter ? `(${STATUS_LABELS[statusFilter]})` : 'insgesamt'}</p>
        </div>
        
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              placeholder="Suchen..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-48 rounded-lg"
            />
            <Button type="submit" variant="outline" className="rounded-lg">
              <Search className="w-4 h-4" />
            </Button>
          </form>

          <Select value={assignmentFilter} onValueChange={(v) => { setAssignmentFilter(v === "all" ? "" : v); setPage(1); }}>
            <SelectTrigger className="w-44 rounded-lg">
              <UserPlus className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Zuweisung" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Leads</SelectItem>
              <SelectItem value="mine">Meine Leads</SelectItem>
              <SelectItem value="unassigned">Nicht zugewiesen</SelectItem>
              <SelectItem value="others">Andere Berater</SelectItem>
            </SelectContent>
          </Select>
          
          {selectedLeads.length > 0 && (
            <Button onClick={openEmailModal} className="rounded-lg bg-blue-600 hover:bg-blue-700">
              <Send className="w-4 h-4 mr-2" />
              E-Mail ({selectedLeads.length})
            </Button>
          )}
          
          {/* Manual Lead Creation Button */}
          <Button 
            onClick={() => setCreateLeadModalOpen(true)}
            className="rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Lead erstellen
          </Button>
        </div>
      </div>

      {/* Lead Table */}
      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : leads.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Keine Leads gefunden
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedLeads.length === leads.length && leads.length > 0}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedLeads(leads.map(l => l.id));
                        } else {
                          setSelectedLeads([]);
                        }
                      }}
                    />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Projekt</TableHead>
                  <TableHead>Kontakt</TableHead>
                  <TableHead>Aktuell</TableHead>
                  <TableHead>Startklar</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zugewiesen</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="w-32">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => {
                  const isAssignedToMe = lead.assigned_to === advisorId;
                  const isAssignedToOther = lead.assigned_to && lead.assigned_to !== advisorId;
                  const isUnassigned = !lead.assigned_to;
                  const canViewLead = isAssignedToMe || isUnassigned;
                  
                  const handleRowClick = () => {
                    if (canViewLead) {
                      onSelectLead(lead.id);
                    } else {
                      toast.error(`Lead ist gesperrt - Zugewiesen an ${lead.assigned_to_name}`);
                    }
                  };
                  
                  return (
                    <TableRow 
                      key={lead.id} 
                      className={`${canViewLead ? 'cursor-pointer hover:bg-slate-50' : 'cursor-not-allowed opacity-60'} ${isAssignedToMe ? 'bg-emerald-50/50' : ''} ${isAssignedToOther ? 'bg-slate-50' : ''}`}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedLeads.includes(lead.id)}
                          onCheckedChange={() => toggleLeadSelection(lead.id)}
                          disabled={isAssignedToOther}
                        />
                      </TableCell>
                      <TableCell onClick={handleRowClick}>
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium">{lead.first_name || "Unbekannt"}</div>
                            {lead.age && <div className="text-xs text-slate-500">{lead.age} Jahre</div>}
                          </div>
                          {/* Voice & Resume indicators */}
                          <div className="flex items-center gap-1">
                            {(lead.voice_message?.has_voice || lead.has_voice_message) && (
                              <Mic className="w-3.5 h-3.5 text-purple-500" title="Sprachnachricht vorhanden" />
                            )}
                            {lead.resume_files && lead.resume_files.length > 0 && (
                              <FileText className="w-3.5 h-3.5 text-amber-500" title="Bewerbungsunterlagen vorhanden" />
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell onClick={handleRowClick}>
                        {getProjectBadge(lead.project_id)}
                      </TableCell>
                      <TableCell>
                        {lead.phone && (
                          <div className="flex items-center gap-2">
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                if (window.callRelayTrigger) {
                                  window.callRelayTrigger(lead.phone, lead.first_name, lead.id);
                                  toast.success(`Anruf wird gestartet: ${lead.phone}`);
                                }
                              }}
                              className="flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700 cursor-pointer hover:underline"
                              title="Klicken um anzurufen"
                            >
                              <Phone className="w-3 h-3" />
                              {lead.phone}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const msg = prompt('SMS-Text eingeben:');
                                if (msg && window.callRelaySms) {
                                  window.callRelaySms(lead.phone, msg, lead.first_name, lead.id);
                                  toast.success(`SMS wird gesendet: ${lead.phone}`);
                                }
                              }}
                              className="p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded"
                              title="SMS senden"
                            >
                              <MessageSquare className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                        {lead.email && (
                          <div className="flex items-center gap-1 text-sm text-slate-500">
                            <Mail className="w-3 h-3 text-slate-400" />
                            {lead.email}
                          </div>
                        )}
                      </TableCell>
                      <TableCell onClick={handleRowClick}>
                        <span className="text-sm text-slate-600">
                          {lead.current_work_status || "—"}
                        </span>
                      </TableCell>
                      <TableCell onClick={handleRowClick}>
                        <span className="text-sm text-slate-600">
                          {lead.start_date || "—"}
                        </span>
                      </TableCell>
                      <TableCell onClick={handleRowClick}>
                        <Badge className={`${STATUS_COLORS[lead.status] || 'bg-slate-100 text-slate-700'}`}>
                          {STATUS_LABELS[lead.status] || lead.status}
                        </Badge>
                      </TableCell>
                      <TableCell onClick={handleRowClick}>
                        {isAssignedToMe ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <User className="w-3 h-3 mr-1" />
                            Mein Lead
                          </Badge>
                        ) : isAssignedToOther ? (
                          <Badge className="bg-red-100 text-red-700">
                            <User className="w-3 h-3 mr-1" />
                            🔒 {lead.assigned_to_name}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400">Offen</span>
                        )}
                      </TableCell>
                      <TableCell onClick={handleRowClick} className="text-sm text-slate-500">
                        {formatDate(lead.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {isUnassigned && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => handleClaimLead(lead.id, e)}
                              disabled={claimingLeadId === lead.id}
                              className="rounded-lg text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
                              title="Übernehmen"
                            >
                              {claimingLeadId === lead.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserPlus className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {isAssignedToMe && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => handleReleaseLead(lead.id, e)}
                              disabled={claimingLeadId === lead.id}
                              className="rounded-lg text-amber-600 border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                              title="Freigeben"
                            >
                              {claimingLeadId === lead.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <UserMinus className="w-4 h-4" />
                              )}
                            </Button>
                          )}
                          {/* Details-Button nur für eigene oder nicht zugewiesene Leads */}
                          {(isAssignedToMe || isUnassigned) ? (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => onSelectLead(lead.id)}
                              className="rounded-lg"
                              title="Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              disabled
                              className="rounded-lg opacity-40 cursor-not-allowed"
                              title={`Gesperrt - Zugewiesen an ${lead.assigned_to_name}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded-lg"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-600">
            Seite {page} von {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="rounded-lg"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              E-Mail an {selectedLeads.length} Lead(s) senden
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-4">
            {/* Left side - Editor */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Vorlage</Label>
                <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Vorlage wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="custom">📝 Eigene Nachricht</SelectItem>
                    {templates.filter(t => t.source === "advisor").length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">Meine Vorlagen</div>
                        {templates.filter(t => t.source === "advisor").map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            ⭐ {template.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {templates.filter(t => t.source !== "advisor").length > 0 && (
                      <>
                        <div className="px-2 py-1 text-xs font-semibold text-slate-500 bg-slate-50">Admin-Vorlagen</div>
                        {templates.filter(t => t.source !== "advisor").map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                {/* Delete button for own templates */}
                {selectedTemplate && templates.find(t => t.id === selectedTemplate && t.source === "advisor") && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteTemplate(selectedTemplate)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs"
                  >
                    🗑️ Vorlage löschen
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <Label>Betreff</Label>
                <Input
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="E-Mail Betreff..."
                  className="rounded-lg"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Inhalt</Label>
                  {/* Save as Template Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSaveTemplateModalOpen(true)}
                    disabled={!emailSubject || !emailContent}
                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 text-xs"
                  >
                    💾 Als Vorlage speichern
                  </Button>
                </div>
                <Textarea
                  value={emailContent}
                  onChange={(e) => setEmailContent(e.target.value)}
                  placeholder="Hallo %vorname%,..."
                  className="rounded-lg min-h-[200px] font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Platzhalter: %vorname%, %nachname%, %email%, %telefon%
                </p>
              </div>
            </div>

            {/* Right side - Preview */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Vorschau (wie beim Empfänger)
              </Label>
              <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
                {/* Email Header Preview */}
                <div className="bg-slate-50 border-b border-slate-200 p-3 text-sm">
                  <div className="flex gap-2">
                    <span className="text-slate-500 font-medium">Betreff:</span>
                    <span className="text-slate-700">
                      {emailSubject
                        .replace(/%vorname%/gi, "Max")
                        .replace(/%nachname%/gi, "Mustermann")
                        .replace(/%email%/gi, "max@beispiel.de")
                        .replace(/%telefon%/gi, "+49 123 456789")
                        .replace(/\{\{name\}\}/gi, "Max Mustermann")
                        .replace(/\{name\}/gi, "Max Mustermann")
                        || "(Kein Betreff)"}
                    </span>
                  </div>
                </div>
                {/* Email Body Preview - renders HTML properly */}
                <div 
                  className="p-4 min-h-[300px] max-h-[400px] overflow-y-auto prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ 
                    __html: emailContent
                      ? emailContent
                          .replace(/%vorname%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Max</strong>")
                          .replace(/%nachname%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Mustermann</strong>")
                          .replace(/%email%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>max@beispiel.de</strong>")
                          .replace(/%telefon%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>+49 123 456789</strong>")
                          .replace(/%alter%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>25</strong>")
                          .replace(/%status%/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Neu</strong>")
                          .replace(/\{\{name\}\}/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Max Mustermann</strong>")
                          .replace(/\{name\}/gi, "<strong style='background:#fef3c7;padding:0 4px;border-radius:2px;'>Max Mustermann</strong>")
                      : "<p style='color:#94a3b8;font-style:italic;'>Vorschau erscheint hier...</p>"
                  }}
                />
              </div>
              <p className="text-xs text-slate-500">
                Platzhalter werden mit Beispieldaten angezeigt. Im echten Versand werden die tatsächlichen Lead-Daten eingesetzt.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailModalOpen(false)} className="rounded-lg">
              Abbrechen
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={isSendingEmail || !emailSubject || !emailContent}
              className="rounded-lg bg-blue-600 hover:bg-blue-700"
            >
              {isSendingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Template Modal */}
      <Dialog open={saveTemplateModalOpen} onOpenChange={setSaveTemplateModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              💾 Vorlage speichern
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name der Vorlage</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="z.B. Einladung zum Gespräch"
                className="rounded-lg"
              />
            </div>
            <div className="text-sm text-slate-500">
              <p><strong>Betreff:</strong> {emailSubject || "(kein Betreff)"}</p>
              <p className="mt-1"><strong>Inhalt:</strong> {emailContent?.substring(0, 100) || "(kein Inhalt)"}...</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveTemplateModalOpen(false)} className="rounded-lg">
              Abbrechen
            </Button>
            <Button
              onClick={handleSaveTemplate}
              disabled={isSavingTemplate || !templateName}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700"
            >
              {isSavingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Lead Creation Modal */}
      <Dialog open={createLeadModalOpen} onOpenChange={setCreateLeadModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Lead manuell erstellen
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Required Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={newLead.first_name}
                  onChange={(e) => setNewLead({ ...newLead, first_name: e.target.value })}
                  placeholder="Vor- und Nachname"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  E-Mail <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                  placeholder="email@beispiel.de"
                  className="rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Telefon <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="tel"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                  placeholder="+49 123 456789"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Alter</Label>
                <Input
                  value={newLead.age}
                  onChange={(e) => setNewLead({ ...newLead, age: e.target.value })}
                  placeholder="z.B. 25"
                  className="rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Wohnort</Label>
                <Input
                  value={newLead.wohnort}
                  onChange={(e) => setNewLead({ ...newLead, wohnort: e.target.value })}
                  placeholder="z.B. Berlin"
                  className="rounded-lg"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Startklar ab</Label>
                <Input
                  value={newLead.start_date}
                  onChange={(e) => setNewLead({ ...newLead, start_date: e.target.value })}
                  placeholder="z.B. Sofort, Ab 01.04."
                  className="rounded-lg"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Job-Typ</Label>
                <Select 
                  value={newLead.job_type} 
                  onValueChange={(value) => setNewLead({ ...newLead, job_type: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Job-Typ wählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="promotour">Promotour</SelectItem>
                    <SelectItem value="messe">Messe</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="verkauf">Verkauf</SelectItem>
                    <SelectItem value="nebenjob">Nebenjob</SelectItem>
                    <SelectItem value="studentenjob">Studentenjob</SelectItem>
                    <SelectItem value="sonstiges">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Verfügbare Tage</Label>
                <Input
                  value={newLead.available_days}
                  onChange={(e) => setNewLead({ ...newLead, available_days: e.target.value })}
                  placeholder="z.B. Mo-Fr, Wochenende"
                  className="rounded-lg"
                />
              </div>
            </div>

            {/* Qualification Dropdowns - Optional fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Reisebereit</Label>
                <Select 
                  value={newLead.travel_ready === true ? "ja" : newLead.travel_ready === false ? "nein" : ""} 
                  onValueChange={(value) => setNewLead({ ...newLead, travel_ready: value === "ja" ? true : value === "nein" ? false : null })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Nicht angegeben" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ja">Ja</SelectItem>
                    <SelectItem value="nein">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Vollzeit</Label>
                <Select 
                  value={newLead.full_time === true ? "ja" : newLead.full_time === false ? "nein" : ""} 
                  onValueChange={(value) => setNewLead({ ...newLead, full_time: value === "ja" ? true : value === "nein" ? false : null })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Nicht angegeben" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ja">Ja</SelectItem>
                    <SelectItem value="nein">Nein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Zoom-Call</Label>
                <Select 
                  value={newLead.zoom_call_ok} 
                  onValueChange={(value) => setNewLead({ ...newLead, zoom_call_ok: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue placeholder="Nicht angegeben" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ja, Video-Call passt">Ja, Video-Call passt</SelectItem>
                    <SelectItem value="Nein, lieber telefonieren">Nein, lieber telefonieren</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Status</Label>
                <Select 
                  value={newLead.status} 
                  onValueChange={(value) => setNewLead({ ...newLead, status: value })}
                >
                  <SelectTrigger className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Interne Notizen</Label>
              <Textarea
                value={newLead.notes}
                onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                placeholder="Zusätzliche Informationen zum Lead..."
                className="rounded-lg min-h-[80px]"
              />
            </div>

            {/* Info Box */}
            <div className="bg-emerald-50 border border-emerald-200 p-3 rounded-lg text-sm">
              <p className="text-emerald-700">
                <strong>Hinweis:</strong> Dieser Lead wird automatisch Ihnen zugewiesen.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setCreateLeadModalOpen(false);
                resetNewLeadForm();
              }}
              className="rounded-lg"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleCreateLead}
              disabled={isCreatingLead || !newLead.first_name || !newLead.email || !newLead.phone}
              className="rounded-lg bg-emerald-600 hover:bg-emerald-700"
            >
              {isCreatingLead ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Erstelle...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Lead erstellen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Lead Detail Component
const LeadDetail = ({ authToken, leadId, onBack }) => {
  const [lead, setLead] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [events, setEvents] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [voiceMessage, setVoiceMessage] = useState(null);
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Editable fields
  const [editData, setEditData] = useState({
    first_name: "",
    age: "",
    phone: "",
    email: "",
    wohnort: "",
    start_date: "",
    job_type: "",
    available_days: "",
    travel_ready: null,
    full_time: null,
    zoom_call_ok: "",
    promo_experience: "",
    people_contact: null,
    flexibility: null,
    current_work_status: "",
    status: "new",
    notes: "",
    project_id: ""
  });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchLeadDetail();
  }, [leadId]);

  const fetchLeadDetail = async () => {
    setIsLoading(true);
    try {
      const [detailRes, historyRes] = await Promise.all([
        axios.get(`${API}/advisor/leads/${leadId}`, getAuthHeaders()),
        axios.get(`${API}/advisor/leads/${leadId}/email-history`, getAuthHeaders())
      ]);
      
      const leadData = detailRes.data.lead;
      setLead(leadData);
      setTranscript(detailRes.data.transcript);
      setEvents(detailRes.data.events || []);
      setEmailHistory(historyRes.data.email_history || []);
      setVoiceMessage(detailRes.data.voice_message || null);
      
      // Initialize editable fields
      console.log("Lead data received:", {
        travel_ready: leadData.travel_ready,
        full_time: leadData.full_time,
        job_type: leadData.job_type,
        promo_experience: leadData.promo_experience
      });
      
      // Helper: treat null/undefined as false to match Admin behavior
      const toBoolWithDefault = (val) => {
        if (val === true || val === "true" || val === 1 || val === "1") return true;
        // Everything else (false, null, undefined, "") is treated as false
        return false;
      };
      
      setEditData({
        first_name: leadData.first_name || "",
        age: leadData.age || "",
        phone: leadData.phone || "",
        email: leadData.email || "",
        wohnort: leadData.wohnort || leadData.city || "",
        start_date: leadData.start_date || "",
        job_type: leadData.job_type || "",
        available_days: leadData.available_days || "",
        travel_ready: toBoolWithDefault(leadData.travel_ready),
        full_time: toBoolWithDefault(leadData.full_time),
        zoom_call_ok: leadData.zoom_call_ok || "",
        promo_experience: leadData.promo_experience || "",
        people_contact: toBoolWithDefault(leadData.people_contact),
        flexibility: toBoolWithDefault(leadData.flexibility),
        current_work_status: leadData.current_work_status || "",
        status: leadData.status || "new",
        notes: leadData.notes || "",
        project_id: leadData.project_id || "hypefactory"
      });
    } catch (error) {
      toast.error("Fehler beim Laden des Leads");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Don't send project_id unless it was explicitly changed by the user
      const dataToSend = { ...editData };
      if (lead && lead.project_id && dataToSend.project_id === lead.project_id) {
        // project_id hasn't changed, don't send it
        delete dataToSend.project_id;
      }
      await axios.put(`${API}/advisor/leads/${leadId}`, dataToSend, getAuthHeaders());
      toast.success("Lead gespeichert!");
      // Update local lead data
      setLead(prev => ({ ...prev, ...editData }));
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setIsSaving(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="text-center py-12 text-slate-500">
        Lead nicht gefunden
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="rounded-lg">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zurück
        </Button>
        <Button onClick={handleSave} disabled={isSaving} className="rounded-lg bg-emerald-600 hover:bg-emerald-700">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Speichern
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-slate-400" />
                Bewerber-Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Name</Label>
                  <Input
                    value={editData.first_name}
                    onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
                    className="rounded-lg"
                    placeholder="Name eingeben"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Alter</Label>
                  <Input
                    value={editData.age}
                    onChange={(e) => setEditData({ ...editData, age: e.target.value })}
                    className="rounded-lg"
                    placeholder="z.B. 25"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Wohnort</Label>
                  <Input
                    value={editData.wohnort}
                    onChange={(e) => setEditData({ ...editData, wohnort: e.target.value })}
                    className="rounded-lg"
                    placeholder="z.B. Berlin"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Startklar ab</Label>
                  <Input
                    value={editData.start_date}
                    onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                    className="rounded-lg"
                    placeholder="z.B. Sofort"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Telefon</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="rounded-lg flex-1"
                      placeholder="+49 123 456789"
                    />
                    {editData.phone && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (window.callRelayTrigger) {
                              window.callRelayTrigger(editData.phone, lead.first_name, lead.id);
                              toast.success(`Anruf wird gestartet: ${editData.phone}`);
                            }
                          }}
                          className="text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                          title="Anrufen"
                        >
                          <Phone className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const msg = prompt('SMS-Text eingeben:');
                            if (msg && window.callRelaySms) {
                              window.callRelaySms(editData.phone, msg, lead.first_name, lead.id);
                              toast.success(`SMS wird gesendet: ${editData.phone}`);
                            }
                          }}
                          className="text-blue-600 border-blue-200 hover:bg-blue-50"
                          title="SMS senden"
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">E-Mail</Label>
                  <Input
                    value={editData.email}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="rounded-lg"
                    placeholder="email@beispiel.de"
                  />
                </div>
              </div>

              {/* Source (read-only) */}
              <div className="pt-2 border-t border-slate-100">
                <Label className="text-xs text-slate-400 uppercase">Quelle</Label>
                <p className="font-medium text-slate-600">{lead.source === "chatbot" ? "Chatbot" : lead.source || "-"}</p>
              </div>

              {/* Duplicate Warning */}
              {lead.is_duplicate && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-700">
                    <span className="text-amber-500">⚠️</span>
                    <span className="font-medium">DUPLIKAT</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-1">
                    Dieser Lead existierte bereits (gleiche Telefon/E-Mail). Original-Lead ID: {lead.duplicate_of?.substring(0, 8)}...
                  </p>
                </div>
              )}

              {/* Appointment Info */}
              {lead.appointment_date && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-700 mb-2">
                    <Calendar className="w-4 h-4" />
                    <span className="font-medium">Termin</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-blue-500">Datum:</span> {lead.appointment_date}
                    </div>
                    <div>
                      <span className="text-blue-500">Uhrzeit:</span> {lead.appointment_time || "-"}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Qualifizierung - Editable */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" />
                Qualifizierung
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Job-Typ</Label>
                  <Select 
                    value={editData.job_type} 
                    onValueChange={(value) => setEditData({ ...editData, job_type: value })}
                  >
                    <SelectTrigger className="rounded-lg">
                      <SelectValue placeholder="Job-Typ wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="promotour">Promotour</SelectItem>
                      <SelectItem value="messe">Messe</SelectItem>
                      <SelectItem value="event">Event</SelectItem>
                      <SelectItem value="verkauf">Verkauf</SelectItem>
                      <SelectItem value="nebenjob">Nebenjob</SelectItem>
                      <SelectItem value="studentenjob">Studentenjob</SelectItem>
                      <SelectItem value="sonstiges">Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Verfügbare Tage</Label>
                  <Input
                    value={editData.available_days}
                    onChange={(e) => setEditData({ ...editData, available_days: e.target.value })}
                    className="rounded-lg"
                    placeholder="z.B. Mo-Fr, Wochenende"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Reisebereit</Label>
                  <Select 
                    value={editData.travel_ready === true ? "ja" : "nein"} 
                    onValueChange={(value) => setEditData({ ...editData, travel_ready: value === "ja" })}
                  >
                    <SelectTrigger className="rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ja">Ja</SelectItem>
                      <SelectItem value="nein">Nein</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Vollzeit</Label>
                  <Select 
                    value={editData.full_time === true ? "ja" : "nein"} 
                    onValueChange={(value) => setEditData({ ...editData, full_time: value === "ja" })}
                  >
                    <SelectTrigger className="rounded-lg">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ja">Ja</SelectItem>
                      <SelectItem value="nein">Nein</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Zoom-Call</Label>
                  <Select 
                    value={editData.zoom_call_ok} 
                    onValueChange={(value) => setEditData({ ...editData, zoom_call_ok: value })}
                  >
                    <SelectTrigger className="rounded-lg">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ja, Video-Call passt">Ja, Video-Call passt</SelectItem>
                      <SelectItem value="Nein, lieber telefonieren">Nein, lieber telefonieren</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400 uppercase">Promo-Erfahrung</Label>
                  <Select 
                    value={editData.promo_experience} 
                    onValueChange={(value) => setEditData({ ...editData, promo_experience: value })}
                  >
                    <SelectTrigger className="rounded-lg">
                      <SelectValue placeholder="—" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Ja, offener Typ">Ja, offener Typ</SelectItem>
                      <SelectItem value="Nein, keine Erfahrung">Nein, keine Erfahrung</SelectItem>
                      <SelectItem value="Etwas Erfahrung">Etwas Erfahrung</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Additional qualification fields - Editable */}
              <div className="pt-4 border-t border-slate-100">
                <Label className="text-xs text-slate-400 uppercase mb-3 block">Weitere Angaben</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400 uppercase">Menschenkontakt</Label>
                    <Select 
                      value={editData.people_contact === true ? "ja" : "nein"} 
                      onValueChange={(value) => setEditData({ ...editData, people_contact: value === "ja" })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nein">Nein</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-400 uppercase">Flexibel</Label>
                    <Select 
                      value={editData.flexibility === true ? "ja" : "nein"} 
                      onValueChange={(value) => setEditData({ ...editData, flexibility: value === "ja" })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ja">Ja</SelectItem>
                        <SelectItem value="nein">Nein</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1 col-span-2">
                    <Label className="text-xs text-slate-400 uppercase">Aktueller Status</Label>
                    <Select 
                      value={editData.current_work_status} 
                      onValueChange={(value) => setEditData({ ...editData, current_work_status: value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="—" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Auf Jobsuche">Auf Jobsuche</SelectItem>
                        <SelectItem value="Berufstätig">Berufstätig</SelectItem>
                        <SelectItem value="Student">Student</SelectItem>
                        <SelectItem value="Schüler">Schüler</SelectItem>
                        <SelectItem value="Selbstständig">Selbstständig</SelectItem>
                        <SelectItem value="Arbeitslos">Arbeitslos</SelectItem>
                        <SelectItem value="Sonstiges">Sonstiges</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chat Transcript */}
          {transcript && transcript.transcript_json && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-blue-500" />
                  Chat-Verlauf
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  <div className="space-y-3">
                    {transcript.transcript_json.map((msg, idx) => (
                      <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[80%] p-3 rounded-lg ${
                          msg.role === 'user' 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-slate-100 text-slate-700'
                        }`}>
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p className="text-xs opacity-60 mt-1">{formatDate(msg.timestamp)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}

          {/* Voice Message */}
          {voiceMessage && voiceMessage.has_voice && (
            <Card className="bg-white border-slate-200 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mic className="w-5 h-5 text-purple-500" />
                  Sprachnachricht
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Audio Player */}
                <div className="flex items-center gap-4 p-4 bg-purple-50 rounded-lg">
                  <audio 
                    id="advisor-voice-audio-player"
                    src={`${API}/voice/audio/${voiceMessage.audio_path}`}
                    onEnded={() => setIsPlayingVoice(false)}
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    className="w-12 h-12 rounded-full border-purple-300 hover:bg-purple-100"
                    onClick={() => {
                      const audio = document.getElementById('advisor-voice-audio-player');
                      if (isPlayingVoice) {
                        audio.pause();
                        setIsPlayingVoice(false);
                      } else {
                        audio.play();
                        setIsPlayingVoice(true);
                      }
                    }}
                    data-testid="advisor-play-voice-btn"
                  >
                    {isPlayingVoice ? (
                      <Pause className="w-6 h-6 text-purple-600" />
                    ) : (
                      <Play className="w-6 h-6 text-purple-600 ml-1" />
                    )}
                  </Button>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-700">Audio-Aufnahme</p>
                    <p className="text-xs text-slate-500">
                      {voiceMessage.created_at ? new Date(voiceMessage.created_at).toLocaleString('de-DE') : 'Unbekannt'}
                    </p>
                  </div>
                </div>
                
                {/* Transcript */}
                {voiceMessage.transcript && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs font-medium text-slate-500 mb-2">Transkript:</p>
                    <p className="text-sm text-slate-700 italic">"{voiceMessage.transcript}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Resume/CV Files - Berater */}
          {lead?.resume_files && lead.resume_files.length > 0 && (
            <Card className="bg-white border-slate-200 border-amber-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Bewerbungsunterlagen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lead.resume_files.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                          {file.filename}
                        </p>
                        <p className="text-xs text-slate-500">
                          {file.uploaded_at ? new Date(file.uploaded_at).toLocaleString('de-DE') : ''}
                          {file.size ? ` • ${(file.size / 1024).toFixed(0)} KB` : ''}
                        </p>
                      </div>
                    </div>
                    <a
                      href={`${API}/resume/file/${file.storage_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar - Status, Zeitstempel, Aktivität */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle>Status & Projekt</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs text-slate-400 uppercase">Aktueller Status:</Label>
                <Select value={editData.status} onValueChange={(value) => setEditData({ ...editData, status: value })}>
                  <SelectTrigger className="rounded-lg">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Projekt wechseln */}
              <div className="space-y-2">
                <Label className="text-xs text-slate-400 uppercase">Projekt:</Label>
                <Select 
                  value={editData.project_id} 
                  onValueChange={(value) => setEditData({ ...editData, project_id: value })}
                >
                  <SelectTrigger className="rounded-lg" data-testid="project-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hypefactory">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                        HypeFactory
                      </div>
                    </SelectItem>
                    <SelectItem value="factoryhype">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                        FactoryHype
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Bewerber ins andere Projekt verschieben, wenn er dort besser qualifiziert ist.
                </p>
              </div>
              
              <Button 
                onClick={() => { setEditData(prev => ({ ...prev, status: "contacted" })); }}
                disabled={isSaving || editData.status === "contacted"}
                className="w-full rounded-lg bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600"
              >
                <Phone className="w-4 h-4 mr-2" />
                Als kontaktiert markieren
              </Button>
            </CardContent>
          </Card>

          {/* Timestamps */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Zeitstempel
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-xs text-slate-400 uppercase">Erstellt</Label>
                <p className="font-medium text-slate-700">{formatDate(lead.created_at)}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-400 uppercase">Zuletzt aktualisiert</Label>
                <p className="font-medium text-slate-700">{formatDate(lead.updated_at)}</p>
              </div>
            </CardContent>
          </Card>

          {/* Activity */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" />
                Aktivität
              </CardTitle>
            </CardHeader>
            <CardContent>
              {events.length > 0 ? (
                <ScrollArea className="h-48">
                  <div className="space-y-3">
                    {events.slice().reverse().map((event, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-slate-200 pl-3">
                        <p className="text-slate-700">
                          {event.event_type === "status_change" ? "Status geändert →" : 
                           event.event_type === "lead_claimed" ? "Lead übernommen" :
                           event.event_type === "lead_released" ? "Lead freigegeben" :
                           event.event_type === "lead_created" ? "Lead erstellt" :
                           event.event_type === "notes_updated" ? "Notizen aktualisiert" :
                           event.event_type}
                        </p>
                        <p className="text-xs text-slate-400">{formatDate(event.created_at)} • {event.actor}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine Aktivitäten</p>
              )}
            </CardContent>
          </Card>

          {/* Notizen */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-slate-400" />
                Notizen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                placeholder="Notizen zum Lead eingeben..."
                className="rounded-lg min-h-[120px]"
              />
              <Button 
                onClick={handleSave} 
                disabled={isSaving}
                className="w-full rounded-lg"
                variant="outline"
              >
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Notizen speichern
              </Button>
            </CardContent>
          </Card>

          {/* Email History */}
          <Card className="bg-white border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                E-Mail Verlauf
                {emailHistory.length > 0 && (
                  <Badge variant="secondary">{emailHistory.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {emailHistory.length > 0 ? (
                <ScrollArea className="h-48">
                  <div className="space-y-3">
                    {emailHistory.map((email, idx) => (
                      <div key={idx} className="text-sm border-b border-slate-100 pb-2 last:border-0">
                        <p className="font-medium">{email.subject}</p>
                        <p className="text-xs text-slate-400">{formatDate(email.sent_at)}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <p className="text-slate-500 text-center py-4">Keine E-Mails gesendet</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Main Advisor Page
export default function AdvisorPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [advisor, setAdvisor] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  
  // Notification State
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    return localStorage.getItem("advisor_notifications_enabled") === "true";
  });

  useLeadNotifications(authToken, notificationsEnabled);

  const toggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    localStorage.setItem("advisor_notifications_enabled", newValue.toString());
    
    if (newValue) {
      if (Notification.permission === "default") {
        Notification.requestPermission().then(permission => {
          if (permission === "granted") {
            toast.success("🔔 Lead-Benachrichtigungen aktiviert!");
          }
        });
      } else if (Notification.permission === "granted") {
        toast.success("🔔 Lead-Benachrichtigungen aktiviert!");
      }
    } else {
      toast.info("🔕 Lead-Benachrichtigungen deaktiviert");
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("advisor_token");
    if (token) {
      verifyToken(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API}/advisor/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAuthenticated(true);
      setAuthToken(token);
      setAdvisor({
        id: response.data.advisor_id,
        name: response.data.name,
        email: response.data.email
      });
    } catch (error) {
      localStorage.removeItem("advisor_token");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (token, advisorData) => {
    setAuthToken(token);
    setAdvisor({
      id: advisorData.id,
      name: advisorData.name,
      email: advisorData.email
    });
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("advisor_token");
    localStorage.removeItem("advisor_name");
    localStorage.removeItem("advisor_email");
    setIsAuthenticated(false);
    setAuthToken(null);
    setAdvisor(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <AdvisorLogin onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-900">Factory-Hype</h1>
              <p className="text-xs text-slate-500">Berater Dashboard</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* IONOS Posteingang Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://mail.ionos.de', '_blank')}
              className="flex items-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
              title="Posteingang öffnen (IONOS Webmail)"
            >
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">Posteingang</span>
              <span className="text-xs text-slate-500 hidden md:inline">(chat@viralehooks.de)</span>
            </Button>

            {/* Notification Toggle */}
            <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
              {notificationsEnabled ? (
                <Bell className="w-4 h-4 text-green-600" />
              ) : (
                <BellOff className="w-4 h-4 text-slate-400" />
              )}
              <span className="text-xs font-medium text-slate-600">Lead-Alarm</span>
              <Button
                variant={notificationsEnabled ? "default" : "outline"}
                size="sm"
                onClick={toggleNotifications}
                className={`h-7 px-2 text-xs ${
                  notificationsEnabled 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'text-slate-500'
                }`}
              >
                {notificationsEnabled ? (
                  <><Volume2 className="w-3 h-3 mr-1" />AN</>
                ) : (
                  <><VolumeX className="w-3 h-3 mr-1" />AUS</>
                )}
              </Button>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-2">
              <div className="text-right">
                <p className="text-sm font-medium text-slate-900">{advisor?.name}</p>
                <p className="text-xs text-slate-500">Berater</p>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={handleLogout} 
                className="text-slate-400 hover:text-red-600 hover:bg-red-50"
                data-testid="advisor-logout-btn"
                title="Abmelden"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {selectedLeadId ? (
          <LeadDetail 
            authToken={authToken} 
            leadId={selectedLeadId} 
            onBack={() => setSelectedLeadId(null)} 
          />
        ) : (
          <LeadList 
            authToken={authToken} 
            onSelectLead={(id) => setSelectedLeadId(id)}
            advisorId={advisor?.id}
            advisorName={advisor?.name}
          />
        )}
      </main>
    </div>
  );
}
